import * as yup from "yup";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

import {
  MULTIMEDIA_PLACEHOLDER,
  MULTIMEDIA_MIME_TYPES,
  FILE_ALREADY_EXISTS,
  OCN_ALREADY_EXISTS,
} from "../../../utils/constants";

import ReactSelect from "../../../components/common/ReactSelect";
import Datepicker from "../../../components/common/Datepicker";
import FileUpload from "../../../components/common/FileUpload";
import Textbox from "../../../components/common/Textbox";
import Toaster from "../../../components/common/Toaster";
import Modal from "../../../components/common/Modal";

import CorFormsForm from "./CorForms/CorFormsForm";
import CorTaxTypes from "./CorTaxTypes";

import complianceService from "../../../services/complianceService";
import taxService from "../../../services/taxService";
import moment from "moment";

const CorForm = (props) => {
  const { id, data, account, isLoading, setLoading, setShow, onSuccess } =
    props;

  const schema = yup.object({
    id: yup.string(),
    is_main_branch: yup.bool(),
    ocn: yup.string().required("Please enter OCN."),
    date_generated: yup.string().required("Please select date generated."),
    tax_types: yup.array().when("id", {
      is: (value) => !value,
      then: yup.array().min(1, "Please add at least one tax type."),
      otherwise: yup.array().min(0),
    }),
    cor_forms: yup.array().when("id", {
      is: (value) => !value,
      then: yup.array().min(1, "Please add at least one tax type."),
      otherwise: yup.array().min(0),
    }),
    rdo: yup.object().shape({
      label: yup.string().required("Please select RDO."),
      value: yup.string().required("Please select RDO."),
    }),
    files: yup
      .mixed()
      .test(
        "is-valid-file-name",
        "A string in a file name must match with the first name.",
        (value) => isValidFileName(value)
      ),
  });

  const [rdos, setRdos] = useState([]);
  const [corForms, setCorForms] = useState([]);
  const [taxTypes, setTaxTypes] = useState([]);
  const [dateGenerated, setDateGenerated] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [fileExists, setFileExists] = useState(false);
  const [ocnExists, setOcnExists] = useState(false);

  const [dateToday] = useState(
    Date.parse(moment(new Date(), "MM-DD-yyyy").toISOString())
  );

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (info) => {
    try {
      setLoading(true);

      const cor = { ...info };
      cor["account_id"] = id;
      cor["rdo"] = info.rdo.value;

      const result = await complianceService.saveCORAsync(cor);

      const item = { ...cor };
      item["id"] = result.id;
      item["status"] = 1;
      item["status_type_value"] = "Active";
      item["ocn_value"] = info.ocn;
      item["date_generated_value"] = moment(info.date_generated).format();
      item["date_generated"] = moment(info.date_generated).format("LL");

      item["ocn"] = (
        <div className="text-sm">
          <div>
            <Link
              className="text-gray-500 hover:text-blue-500"
              to={`/certificate-of-registrations/${item.id}`}
            >
              {item.ocn}
            </Link>
          </div>
          <span className="text-gray-400 text-xs">
            {info.is_main_branch ? "Main" : "Branch"}
          </span>
        </div>
      );

      item["status_type"] = (
        <div className="flex items-center">
          <div className="rounded-full h-2 w-2 flex items-center bg-green-400 justify-center" />
          <span className="ml-3">Active</span>
        </div>
      );

      if (result.s3_reference_names && result.s3_reference_names.length > 0) {
        item["s3_reference_name"] = result.s3_reference_names[0];
        item["document_name"] = result.files[0].file_name;
      }

      Toaster.Success("COR Saved Successfully");
      onSuccess(item);

      setShow(false);
      setLoading(false);
    } catch (ex) {
      if (ex.response && ex.response.data.message === FILE_ALREADY_EXISTS) {
        setFileExists(true);
        setLoading(false);
        return;
      }

      if (ex.response && ex.response.data.message === OCN_ALREADY_EXISTS) {
        setOcnExists(true);
        setLoading(false);
        return;
      }

      Toaster.Error("Something went wrong.");
      setLoading(false);
    }
  };

  const fetchRdos = useCallback(async (mounted) => {
    try {
      const result = await taxService.getRevenueDistrictOfficesAsync();

      const options = result.map((item) => {
        const option = {};
        option["label"] = item;
        option["value"] = item;

        return option;
      });

      if (mounted) setRdos(options);
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    fetchRdos(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchRdos]);

  const handleTaxType = (items) => setTaxTypes(items);

  const handleDelete = (item) => {
    const items = taxTypes.filter((i) => i !== item);
    setTaxTypes(items);
  };

  const isValidFileName = (file) => {
    if (file === undefined) return true;

    let first_name = account.first_name;
    let file_name = file[0].name;

    first_name = first_name.toLowerCase();
    file_name = file_name.toLowerCase();

    return file_name.includes(first_name);
  };

  useEffect(() => {
    setValue("cor_forms", corForms);
    clearErrors("cor_forms");
  }, [corForms, setValue, clearErrors]);

  useEffect(() => {
    if (data) {
      const generated = moment(data.date_generated_value).format("MM-DD-yyyy");
      const selected_rdo = {
        label: data.rdo,
        value: data.rdo,
      };

      setValue("id", data.id);
      setValue("ocn", data.ocn_value);
      setValue("is_main_branch", data.is_main_branch);
      setValue("date_generated", generated);
      setValue("rdo", selected_rdo);

      setDateGenerated(generated);
    }
  }, [data, setValue]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
        <div className="grid grid-cols-5 gap-6">
          <div className="col-span-5 lg:col-span-3">
            <Textbox
              id="ocn-textbox"
              name="ocn"
              type="text"
              title="OCN"
              register={register("ocn")}
              errors={errors}
            />
            {ocnExists && (
              <div className="pt-2">
                <span className="text-sm text-red-400">
                  {OCN_ALREADY_EXISTS}
                </span>
              </div>
            )}
          </div>
          <div className="col-span-5 lg:col-span-2">
            <Datepicker
              id="date-generated-datepicker"
              name="date_generated"
              title="Date Generated"
              dateFormat="MM-DD-yyyy"
              timeFormat={false}
              onDateChange={(date) => setDateGenerated(date)}
              initialValue={dateToday}
              value={dateGenerated}
              setValue={setValue}
              clearErrors={clearErrors}
              register={register("date_generated")}
              errors={errors}
            />
          </div>
        </div>
        <div className="grid grid-cols-2">
          <div className="col-span-2">
            <ReactSelect
              id="rdo-dropdown"
              name="rdo"
              title="RDO"
              options={rdos}
              control={control}
              register={register("rdo")}
              errors={errors}
            />
          </div>
        </div>
        {!data && (
          <CorTaxTypes
            taxTypes={taxTypes}
            errors={errors}
            setCorForms={setCorForms}
            setShowForm={setShowForm}
            onDelete={handleDelete}
          />
        )}
        <div className="grid grid-cols-2">
          <div className="col-span-2">
            <input
              id="is-main-branch-checkbox"
              name="is_main_branch"
              type="checkbox"
              defaultChecked={true}
              {...register("is_main_branch")}
              className="text-blue-500 cursor-pointer border-gray-300 rounded"
            />
            <label className="ml-3 text-sm text-gray-400">Main Branch</label>
          </div>
        </div>
        {!data && (
          <div className="grid grid-cols-2">
            <div className="col-span-2">
              <FileUpload
                id="files-dropdown"
                name="files"
                title="Files"
                setValue={setValue}
                clearErrors={clearErrors}
                register={register("files")}
                errors={errors}
                accepts={MULTIMEDIA_MIME_TYPES}
                placeholder={MULTIMEDIA_PLACEHOLDER}
                maxFiles={1}
                maxSize={5242880}
                multiple={false}
              />
              {fileExists && (
                <div className="pt-2">
                  <span className="text-sm text-red-400">
                    {FILE_ALREADY_EXISTS}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 pt-2">
          <div className="col-span-2">
            <button
              type="submit"
              className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
              {!isLoading && <IoSaveOutline className="mr-3" />}
              Save Changes
            </button>
          </div>
        </div>
      </form>
      <Modal
        isOpen={showForm}
        title="Add Tax Types"
        onClose={() => setShowForm(false)}
        borderTop
      >
        <CorFormsForm
          data={taxTypes}
          onSubmit={handleTaxType}
          onClose={setShowForm}
        />
      </Modal>
    </>
  );
};

export default CorForm;
